/**
 * The sidebar's first screen as the person arranged it: a top section of the profiles they reach
 * for, and every other profile folded into a category.
 *
 * Kept JSX-free so `node --test` can load it directly.
 */
import type { ExpandedKey, SidebarView } from "../types";
import { OTHER_KEY, categoryKey } from "../../../shared/wire/sidebar.ts";
import type { SessionGroup } from "./sessionGroups.ts";

/** One fold of profiles: a category of the person's own, or Other. */
export interface ProfileCategory {
	key: ExpandedKey;
	/** Null for Other. */
	id: string | null;
	name: string;
	groups: SessionGroup[];
	/** The person opened it. */
	expanded: boolean;
}

export interface SidebarArrangement {
	top: SessionGroup[];
	categories: ProfileCategory[];
}

/** How many profiles the top section holds, unless more are starred. */
export const TOP_SIZE = 2;

/** What Other is called on screen. */
export const OTHER_NAME = "其他";

/**
 * Starred profiles come first, in star order and whatever their status. The loadable profiles the
 * person used most then fill the top to two. Every other profile goes to its category in profile
 * order: the person's categories in their order, then Other.
 */
export function arrangeSidebar(groups: SessionGroup[], view: SidebarView | null): SidebarArrangement {
	const byProfile = new Map(groups.map((g) => [g.profile, g]));
	const top: SessionGroup[] = [];
	for (const name of view?.stars ?? []) {
		const group = byProfile.get(name);
		if (group && !top.includes(group)) top.push(group);
	}
	const useOf = new Map((view?.uses ?? []).filter((u) => u.conversations > 0).map((u) => [u.profile, u]));
	// A stable sort, so profiles used equally keep profile order.
	const used = groups
		.filter((g) => g.status === "ok" && !top.includes(g) && useOf.has(g.profile))
		.sort((a, b) => {
			const ua = useOf.get(a.profile)!;
			const ub = useOf.get(b.profile)!;
			return ub.conversations - ua.conversations || ub.lastAt - ua.lastAt;
		});
	top.push(...used.slice(0, Math.max(0, TOP_SIZE - top.length)));

	const open = new Set(view?.expanded ?? []);
	const fold = (key: ExpandedKey, id: string | null, name: string): ProfileCategory => ({
		key,
		id,
		name,
		groups: [],
		expanded: open.has(key),
	});
	const own = (view?.categories ?? []).map((c) => fold(categoryKey(c.id), c.id, c.name));
	const other = fold(OTHER_KEY, null, OTHER_NAME);
	for (const group of groups) {
		if (top.includes(group)) continue;
		const placed = view?.placements[group.profile];
		(own.find((c) => c.id === placed) ?? other).groups.push(group);
	}
	// A category the person made shows while empty, so they can still see and manage it.
	return { top, categories: other.groups.length > 0 ? [...own, other] : own };
}
